import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Shirt, MoreHorizontal, Lock, Unlock, Trash2, Search } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Card, CardContent, Badge, Label, Input, ChipFilter,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../../ui'
import {
  getTemporadaPrendas, addTemporadaPrenda, removeTemporadaPrenda,
  setEstatusTemporadaPrenda, getPrendas,
} from '../../../lib/supabase'
import { useTemporadaActiva } from '../pagos/useTemporadaActiva'
import { useDeportes } from '../../../hooks/useDeportes'
import { etiquetaTemporada } from '../../../lib/uniformesMultiTemporada'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { formatMoney } from '../../../utils/helpers'
import { useNavigate } from 'react-router-dom'

// Que prendas van en esta temporada. Se llena A MANO a proposito: se evaluo
// enlazar solas las prendas activas del deporte y el dueno lo rechazo porque
// esconde una decision suya.

const EstatusBadge = ({ estatus }) => (
  <Badge variant={estatus === 'abierta' ? 'success' : 'secondary'}>
    {estatus === 'abierta' ? 'Abierta' : 'Cerrada'}
  </Badge>
)

// El menu de acciones de una prenda de la temporada. Se extrae para no duplicarlo
// entre la lista de movil y la tabla de escritorio (regla 11).
const AccionesPrenda = ({ tp, saving, onEstatus, onQuitar }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Mas acciones" aria-label="Mas acciones">
        <MoreHorizontal className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem disabled={saving} onClick={() => onEstatus(tp)}>
        {tp.estatus === 'abierta'
          ? (<><Lock className="h-4 w-4" /> Cerrar: ya no se pide</>)
          : (<><Unlock className="h-4 w-4" /> Reabrir para pedir</>)}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        disabled={saving}
        onClick={() => onQuitar(tp)}
        className="text-destructive focus:text-destructive"
      >
        <Trash2 className="h-4 w-4" /> Quitar de la temporada
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)

const TemporadaPrendasPage = () => {
  const { temporadaId, temporadas, setTemporadaId } = useTemporadaActiva()
  const { label: deporteLabel } = useDeportes()

  // Chips y no desplegable: son pocas y conocidas, y un select las esconde a todas
  // detras de un clic (ux-rules §13). Es el mismo control que Pedidos y Reparto --
  // tener tres formas distintas de elegir temporada en el mismo modulo obligaba a
  // volver a aprenderlo en cada pantalla.
  const chipsTemporada = (temporadas || [])
    .filter((t) => t.estado === 'abierta' || t.id === temporadaId)
    .map((t) => ({ value: t.id, label: etiquetaTemporada(t, deporteLabel) }))
  const [delaTemporada, setDeLaTemporada] = useState([])
  const [catalogo, setCatalogo] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [buscar, setBuscar] = useState('')
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const navigate = useNavigate()

  const load = useCallback(async () => {
    if (!temporadaId) { setLoading(false); return }
    setLoading(true)
    const [tpRes, prendasRes] = await Promise.all([
      getTemporadaPrendas(temporadaId),
      getPrendas(),
    ])
    setDeLaTemporada(tpRes.data || [])
    setCatalogo(prendasRes.data || [])
    setLoading(false)
  }, [temporadaId])

  useEffect(() => { load() }, [load])

  const yaEstan = new Set(delaTemporada.map((tp) => tp.prenda_id))
  const termino = buscar.trim().toLowerCase()
  const coincide = (nombre) => !termino || (nombre || '').toLowerCase().includes(termino)

  const enTemporada = delaTemporada.filter((tp) => coincide(tp.prendas?.nombre))
  const disponibles = catalogo.filter((p) => !yaEstan.has(p.id) && coincide(p.nombre))

  const handleAgregar = async (prenda) => {
    setSaving(true)
    const { error } = await addTemporadaPrenda({
      temporadaId, prendaId: prenda.id, deporte: prenda.deporte,
    })
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo agregar'); return }
    load()
  }

  const handleQuitar = async (tp) => {
    const ok = await confirm(
      'Quitar de la temporada',
      'Los jugadores dejaran de necesitar esta prenda. Si ya hay tallas capturadas, mejor cierrala en vez de quitarla.',
      { destructive: true },
    )
    if (!ok) return
    setSaving(true)
    const { error } = await removeTemporadaPrenda(tp.id)
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo quitar'); return }
    load()
  }

  const handleEstatus = async (tp) => {
    const nuevo = tp.estatus === 'abierta' ? 'cerrada' : 'abierta'
    setSaving(true)
    const { error } = await setEstatusTemporadaPrenda(tp.id, nuevo)
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo cambiar'); return }
    load()
  }

  return (
    <FeaturedLayout
      title="Prendas de la temporada"
      subtitle="Que prendas se mandan a hacer este ciclo"
      refreshAction={load}
    >
      {/* La temporada es el contexto de la pantalla: aqui SI manda, porque todo lo
          que se hace ocurre dentro de ella. Debajo, la toolbar estandar del
          proyecto (ux-rules §9), la misma de Prendas. */}
      <div className="mb-3 space-y-1.5">
        <Label className="text-xs text-muted-foreground">Temporada</Label>
        <ChipFilter
          ariaLabel="Temporada"
          scroll
          opciones={chipsTemporada}
          valor={temporadaId || ''}
          onChange={(v) => v && setTemporadaId(v)}
        />
      </div>

      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar prenda..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="hidden whitespace-nowrap text-sm text-muted-foreground sm:block">
          <span className="font-medium text-foreground tabular-nums">{enTemporada.length}</span> en la temporada
        </span>
        <Button
          onClick={() => navigate('/admin/uniformes')}
          variant="outline"
          className="h-10 shrink-0"
          aria-label="Ir al catalogo de prendas"
          title="Ir al catalogo de prendas"
        >
          <Shirt className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Catalogo</span>
        </Button>
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
      ) : !temporadaId ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Shirt className="mx-auto mb-3 h-12 w-12 opacity-30" />
            <p>No hay temporadas. Crea una en Administracion para empezar.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* ---- En esta temporada ---- */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">En esta temporada</h3>
            <Card>
              {enTemporada.length === 0 ? (
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  {termino
                    ? 'Ninguna prenda de la temporada coincide con la busqueda.'
                    : 'Todavia no hay prendas en esta temporada. Agregalas del catalogo de la derecha para que los jugadores empiecen a necesitarlas.'}
                </CardContent>
              ) : (
                <>
                  {/* Movil: lista. Los nombres de prenda son texto libre y una tabla
                      de 4 columnas se recorta en silencio bajo el overflow-x-hidden
                      de FeaturedLayout (regla 11). */}
                  <div className="divide-y px-4 sm:hidden">
                    {enTemporada.map((tp) => (
                      <div key={tp.id} className="flex items-start justify-between gap-3 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{tp.prendas?.nombre || 'Prenda'}</p>
                          <p className="truncate text-xs text-muted-foreground">{deporteLabel(tp.deporte)}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <EstatusBadge estatus={tp.estatus} />
                          <AccionesPrenda tp={tp} saving={saving} onEstatus={handleEstatus} onQuitar={handleQuitar} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Escritorio: la tabla completa */}
                  <div className="hidden sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Prenda</TableHead>
                          <TableHead>Deporte</TableHead>
                          <TableHead>Estatus</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {enTemporada.map((tp) => (
                          <TableRow key={tp.id}>
                            <TableCell className="text-sm font-medium">{tp.prendas?.nombre || 'Prenda'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{deporteLabel(tp.deporte)}</Badge>
                            </TableCell>
                            <TableCell><EstatusBadge estatus={tp.estatus} /></TableCell>
                            <TableCell>
                              <AccionesPrenda tp={tp} saving={saving} onEstatus={handleEstatus} onQuitar={handleQuitar} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </Card>
          </div>

          {/* ---- Del catalogo ---- */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Del catalogo</h3>
              <span className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{disponibles.length}</span> disponibles
              </span>
            </div>
            <Card>
              {disponibles.length === 0 ? (
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  {termino ? (
                    'Ninguna prenda del catalogo coincide con la busqueda.'
                  ) : catalogo.length === 0 ? (
                    <>
                      <p className="mb-3">No hay prendas en el catalogo todavia.</p>
                      <Button variant="outline" size="sm" onClick={() => navigate('/admin/uniformes')}>
                        Crear la primera prenda
                      </Button>
                    </>
                  ) : (
                    'Todas las prendas del catalogo ya estan en esta temporada.'
                  )}
                </CardContent>
              ) : (
                <>
                  <div className="divide-y px-4 sm:hidden">
                    {disponibles.map((p) => (
                      <div key={p.id} className="flex items-start justify-between gap-3 py-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{p.nombre}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            <span>{deporteLabel(p.deporte)}</span> · {formatMoney(p.precio)}
                          </p>
                        </div>
                        <Button
                          variant="outline" size="sm" className="h-8 w-8 shrink-0 p-0"
                          disabled={saving} onClick={() => handleAgregar(p)}
                          aria-label={`Agregar ${p.nombre} a la temporada`}
                          title="Agregar a la temporada"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="hidden sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Prenda</TableHead>
                          <TableHead>Deporte</TableHead>
                          <TableHead className="text-right">Precio</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {disponibles.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell className="text-sm font-medium">{p.nombre}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{deporteLabel(p.deporte)}</Badge>
                            </TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{formatMoney(p.precio)}</TableCell>
                            <TableCell>
                              <Button
                                variant="outline" size="sm" className="h-8 px-2"
                                disabled={saving} onClick={() => handleAgregar(p)}
                                aria-label={`Agregar ${p.nombre} a la temporada`}
                                title="Agregar a la temporada"
                              >
                                <Plus className="h-3.5 w-3.5 sm:mr-1.5" />
                                <span className="hidden text-xs sm:inline">Agregar</span>
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      )}
      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default TemporadaPrendasPage
